import { createFileRoute, Link } from "@tanstack/react-router";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import SparklesIcon from "lucide-react/dist/esm/icons/sparkles";
import { useState } from "react";
import { toast } from "sonner";

import { ScenarioFlowCanvas } from "@/components/flow/flow-canvas";
import { PageBody, Panel } from "@/components/shell/page-frame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { ScenarioDefinition, ScenarioStep, StepKind } from "@/features/scenarios/definition";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/scenarios/new")({
  component: ComposeScenarioPage
});

type Phase = "input" | "proposal" | "created";

interface ProposedStep {
  kind: StepKind;
  title: string;
  detail: string;
}

interface Proposal {
  name: string;
  trigger: string;
  steps: ProposedStep[];
}

/** 入力文からそれっぽい設計案を組み立てる（実際は MCP 経由で Claude が生成する）。 */
function buildProposal(prompt: string): Proposal {
  const rainy = prompt.includes("雨");
  return {
    name: prompt.length > 18 ? `${prompt.slice(0, 18)}…` : prompt || "新しいシナリオ",
    trigger: rainy ? "weather/rainy（外部トリガー）" : "schedule/daily",
    steps: [
      {
        kind: "trigger",
        title: rainy ? "雨天を検知" : "条件成立を検知",
        detail: rainy ? "対象エリアの降水を外部APIから受信" : "指定条件でフローを起動"
      },
      {
        kind: "condition",
        title: "配信対象を判定",
        detail: "直近で来店・閲覧のある近隣顧客に絞る"
      },
      {
        kind: "action",
        title: "クーポン配信",
        detail: rainy ? "「雨の日限定10%オフ」を即時配信" : "訴求メールを配信"
      },
      { kind: "action", title: "成果を記録", detail: "開封・クリック・転換をダッシュボードに反映" }
    ]
  };
}

/** 設計案を、ビルダー（キャンバス）が読める ScenarioDefinition に変換する。 */
function toDefinition(proposal: Proposal): ScenarioDefinition {
  const steps: ScenarioStep[] = proposal.steps.map((step, index) => ({
    id: `new-${index}`,
    kind: step.kind,
    title: step.title,
    detail: step.detail,
    entered: 0,
    advanced: 0
  }));
  return { id: "new", segmentId: null, steps, deliverySuggestion: null, improvement: null };
}

/**
 * 新シナリオのAI生成(4-6)。一文から設計案を生成し、既存シナリオと同じビジュアルビルダーで
 * ノード・条件を編集してから、承認 → テスト配信 → 本番。
 */
function ComposeScenarioPage() {
  const [phase, setPhase] = useState<Phase>("input");
  const [prompt, setPrompt] = useState("");
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [name, setName] = useState("");
  // 作り直すたびにキャンバスを作り直す（内部の編集状態をリセット）
  const [revision, setRevision] = useState(0);

  const generate = () => {
    const next = buildProposal(prompt.trim());
    setProposal(next);
    setName(next.name);
    setRevision((value) => value + 1);
    setPhase("proposal");
  };

  return (
    <>
      {/* ビルダーと同じ体裁のヘッダー。フェーズに応じてアクションが変わる。 */}
      <div className="sticky top-(--topbar-h) z-30 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="シナリオ一覧へ戻る"
          render={<Link to="/scenarios" />}
        >
          <ArrowLeftIcon aria-hidden="true" />
        </Button>
        <h1 className="text-lg font-semibold tracking-tight text-fg">シナリオを作成</h1>

        <Stepper phase={phase} />

        {phase === "proposal" ? (
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setPhase("input")}>
              作り直す
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setPhase("created");
                toast.success("少数セグメントでテスト配信を開始しました");
              }}
            >
              承認してテスト配信
            </Button>
          </div>
        ) : null}
      </div>

      {phase === "input" ? (
        <PageBody density="dense" className="mx-auto grid w-full max-w-180 gap-4">
          <Panel title="やりたいことを一文で" description="トリガー・条件・文面はAIが設計します">
            <Textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={3}
              placeholder="例: 雨の日に近隣の常連へ限定クーポンを配信したい"
              className="resize-none"
            />
            <div className="mt-3 flex flex-wrap gap-1.5">
              {["雨の日クーポンを配信", "誕生日月の顧客に特典", "3回購入した人にVIP案内"].map(
                (example) => (
                  <Button
                    key={example}
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => setPrompt(example)}
                  >
                    {example}
                  </Button>
                )
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <Button type="button" onClick={generate} disabled={prompt.trim().length === 0}>
                <SparklesIcon aria-hidden="true" />
                設計案を生成
              </Button>
            </div>
          </Panel>
        </PageBody>
      ) : null}

      {phase === "proposal" && proposal ? (
        <div className="flex flex-col" style={{ height: "calc(100dvh - var(--topbar-h) - 53px)" }}>
          {/* シナリオ名（編集可能）とトリガー。ステップ本体はキャンバスで編集する。 */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2">
            <label htmlFor="scenario-name" className="eyebrow">
              シナリオ名
            </label>
            <Input
              id="scenario-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="h-8 w-64"
            />
            <span className="num rounded-md bg-surface-sunken px-2 py-1 text-xs text-fg-muted">
              {proposal.trigger}
            </span>
            <span className="ml-auto text-xs text-fg-subtle">
              ノードの + から条件・アクションを追加、ノードを選ぶと編集できます
            </span>
          </div>

          <div className="min-h-0 flex-1">
            <ScenarioFlowCanvas key={revision} definition={toDefinition(proposal)} />
          </div>
        </div>
      ) : null}

      {phase === "created" && proposal ? (
        <PageBody density="dense" className="mx-auto grid w-full max-w-180 gap-4">
          <Panel title="テスト配信を開始しました">
            <p className="text-sm text-fg">
              「{name}」を下書きとして作成し、少数セグメント（50人）へテスト配信しました。
              反応を確認してから本番稼働に切り替えられます。
            </p>
            <div className="mt-4 flex items-center gap-2">
              <Button type="button" render={<Link to="/scenarios" />}>
                シナリオ一覧へ
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPhase("input");
                  setPrompt("");
                  setProposal(null);
                }}
              >
                続けて作成
              </Button>
            </div>
          </Panel>
        </PageBody>
      ) : null}
    </>
  );
}

function Stepper({ phase }: { phase: Phase }) {
  const steps = [
    { value: "input", label: "指示" },
    { value: "proposal", label: "設計案" },
    { value: "created", label: "作成" }
  ] as const;

  return (
    <ol className="hidden items-center gap-2 text-xs md:flex">
      {steps.map((step, index) => {
        const active = phase === step.value;
        const done =
          (step.value === "input" && phase !== "input") ||
          (step.value === "proposal" && phase === "created");
        return (
          <li key={step.value} className="flex items-center gap-2">
            <span
              className={cn(
                "num inline-flex size-5 items-center justify-center rounded-full text-2xs font-semibold",
                active
                  ? "bg-brand text-brand-fg"
                  : done
                    ? "bg-success text-white"
                    : "bg-surface-sunken text-fg-muted"
              )}
            >
              {index + 1}
            </span>
            <span className={active ? "font-semibold text-fg" : "text-fg-muted"}>{step.label}</span>
            {index < steps.length - 1 ? (
              <span aria-hidden="true" className="h-px w-6 bg-line" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
